// BankLogoService - Service for managing bank logos
import { SUPABASE_URL, supabase } from '../integrations/supabase/client';
import { getAllBankSvgLogos, getBankSvgPath } from '../utils/bankSvgMappings';

export interface BankLogo {
  id: string;
  bank_name: string;
  logo_url: string;
  logo_storage_path: string;
  created_at: string;
  updated_at: string;
}

export interface BankLogoRequest {
  bank_name: string;
  logo_url: string;
  logo_storage_path: string;
}

class BankLogoService {
  private supabase = supabase;
  private supabaseTableAvailable = true;
  private loggedMissingTableWarning = false;
  private readonly localFallbackTimestamp = new Date(0).toISOString();
  private localLogosCache: BankLogo[] | null = null;

  /**
   * Helpers
   */
  private isBankLogoTableMissing(error: any): boolean {
    if (!error) return false;
    const message = typeof error.message === 'string' ? error.message : '';
    return (
      error.code === 'PGRST205' ||
      message.includes("Could not find the table 'public.bank_logos'")
    );
  }

  private handleTableError(error: any): boolean {
    if (this.isBankLogoTableMissing(error)) {
      this.supabaseTableAvailable = false;
      if (!this.loggedMissingTableWarning) {
        console.warn('Supabase bank_logos table unavailable. Falling back to local logo assets.');
        this.loggedMissingTableWarning = true;
      }
      return true;
    }
    return false;
  }

  private normalizeForAsset(bankName: string): string {
    if (!bankName) return '';
    return bankName
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');
  }

  private toDisplayName(normalizedName: string): string {
    if (!normalizedName) return 'Default';

    return normalizedName
      .split('-')
      .filter(Boolean)
      .map(part => {
        if (part.length <= 3) {
          return part.toUpperCase();
        }
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join(' ');
  }

  private getLocalDefaultLogoUrl(): string {
    return '/placeholder.svg';
  }

  private getLocalLogoUrlByNormalized(normalizedName: string): string | null {
    if (!normalizedName) return null;
    const path = getBankSvgPath(normalizedName);
    return path || null;
  }

  private getLocalLogoUrl(bankName: string): string | null {
    const normalized = this.normalizeForAsset(bankName);
    return this.getLocalLogoUrlByNormalized(normalized);
  }

  private createLocalLogo(bankName: string, normalizedName?: string, explicitLogoUrl?: string): BankLogo {
    const normalized = (normalizedName ?? this.normalizeForAsset(bankName)) || 'default';
    const safeName = bankName || this.toDisplayName(normalized);
    const logoUrl = explicitLogoUrl ?? this.getLocalLogoUrlByNormalized(normalized) ?? this.getLocalDefaultLogoUrl();

    return {
      id: `local-${normalized}`,
      bank_name: safeName,
      logo_url: logoUrl,
      logo_storage_path: logoUrl,
      created_at: this.localFallbackTimestamp,
      updated_at: this.localFallbackTimestamp,
    };
  }

  private ensureLocalLogos(): BankLogo[] {
    if (this.localLogosCache) {
      return this.localLogosCache;
    }

    const logos = getAllBankSvgLogos().map(({ name, path }) => {
      const displayName = this.toDisplayName(name);
      return this.createLocalLogo(displayName, name, path);
    });

    logos.push(this.createLocalLogo('Default', 'default', this.getLocalDefaultLogoUrl()));

    this.localLogosCache = logos;
    return logos;
  }

  private getLocalLogo(bankName: string): BankLogo {
    return this.createLocalLogo(bankName);
  }

  private getLocalLogosMatching(searchTerm: string): BankLogo[] {
    if (!searchTerm.trim()) {
      return [];
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();
    return this.ensureLocalLogos().filter(logo =>
      logo.bank_name.toLowerCase().includes(normalizedSearch)
    );
  }

  /**
   * Get all bank logos
   */
  async getAllBankLogos(): Promise<BankLogo[]> {
    if (!this.supabaseTableAvailable) {
      return this.ensureLocalLogos();
    }

    try {
      const { data, error } = await this.supabase
        .from('bank_logos')
        .select('*')
        .order('bank_name');

      if (error) {
        if (this.handleTableError(error)) {
          return this.ensureLocalLogos();
        }
        console.error('Error fetching bank logos:', error);
        throw error;
      }

      return data || [];
    } catch (err) {
      if (this.handleTableError(err)) {
        return this.ensureLocalLogos();
      }
      console.error('Error in getAllBankLogos:', err);
      return [];
    }
  }

  /**
   * Get bank logo by bank name
   */
  async getBankLogoByBankName(bankName: string): Promise<BankLogo | null> {
    if (!this.supabaseTableAvailable) {
      return this.getLocalLogo(bankName);
    }

    try {
      const { data, error } = await this.supabase
        .from('bank_logos')
        .select('*')
        .eq('bank_name', bankName)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned - bank not found
          return null;
        }

        if (this.handleTableError(error)) {
          return this.getLocalLogo(bankName);
        }

        console.error('Error fetching bank logo:', error);
        throw error;
      }

      return data;
    } catch (err) {
      if (this.handleTableError(err)) {
        return this.getLocalLogo(bankName);
      }
      console.error('Error in getBankLogoByBankName:', err);
      return null;
    }
  }

  /**
   * Search bank logos by name (partial match)
   */
  async searchBankLogos(searchTerm: string): Promise<BankLogo[]> {
    if (!this.supabaseTableAvailable) {
      return this.getLocalLogosMatching(searchTerm);
    }

    try {
      const { data, error } = await this.supabase
        .from('bank_logos')
        .select('*')
        .ilike('bank_name', `%${searchTerm}%`)
        .order('bank_name');

      if (error) {
        if (this.handleTableError(error)) {
          return this.getLocalLogosMatching(searchTerm);
        }
        console.error('Error searching bank logos:', error);
        throw error;
      }

      return data || [];
    } catch (err) {
      if (this.handleTableError(err)) {
        return this.getLocalLogosMatching(searchTerm);
      }
      console.error('Error in searchBankLogos:', err);
      return [];
    }
  }

  /**
   * Create a new bank logo entry
   */
  async createBankLogo(request: BankLogoRequest): Promise<BankLogo | null> {
    if (!this.supabaseTableAvailable) {
      console.warn('createBankLogo called while bank_logos table is unavailable. Returning local fallback.');
      return this.getLocalLogo(request.bank_name);
    }

    try {
      const { data, error } = await this.supabase
        .from('bank_logos')
        .insert(request)
        .select()
        .single();

      if (error) {
        if (this.handleTableError(error)) {
          return this.getLocalLogo(request.bank_name);
        }
        console.error('Error creating bank logo:', error);
        throw error;
      }

      return data;
    } catch (err) {
      if (this.handleTableError(err)) {
        return this.getLocalLogo(request.bank_name);
      }
      console.error('Error in createBankLogo:', err);
      return null;
    }
  }

  /**
   * Update an existing bank logo
   */
  async updateBankLogo(id: string, request: Partial<BankLogoRequest>): Promise<BankLogo | null> {
    if (!this.supabaseTableAvailable) {
      console.warn('updateBankLogo called while bank_logos table is unavailable. Returning local fallback.');
      return request.bank_name ? this.getLocalLogo(request.bank_name) : null;
    }

    try {
      const { data, error } = await this.supabase
        .from('bank_logos')
        .update(request)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (this.handleTableError(error)) {
          return request.bank_name ? this.getLocalLogo(request.bank_name) : null;
        }
        console.error('Error updating bank logo:', error);
        throw error;
      }

      return data;
    } catch (err) {
      if (this.handleTableError(err)) {
        return request.bank_name ? this.getLocalLogo(request.bank_name) : null;
      }
      console.error('Error in updateBankLogo:', err);
      return null;
    }
  }

  /**
   * Delete a bank logo
   */
  async deleteBankLogo(id: string): Promise<boolean> {
    if (!this.supabaseTableAvailable) {
      console.warn('deleteBankLogo called while bank_logos table is unavailable. Operation skipped.');
      return false;
    }

    try {
      const { error } = await this.supabase
        .from('bank_logos')
        .delete()
        .eq('id', id);

      if (error) {
        if (this.handleTableError(error)) {
          return false;
        }
        console.error('Error deleting bank logo:', error);
        throw error;
      }

      return true;
    } catch (err) {
      if (this.handleTableError(err)) {
        return false;
      }
      console.error('Error in deleteBankLogo:', err);
      return false;
    }
  }

  /**
   * Upload a bank logo file to storage
   */
  async uploadBankLogo(file: File, bankName: string): Promise<string | null> {
    try {
      const normalizedName = this.normalizeBankName(bankName);
      const fileExt = file.name.split('.').pop();
      const fileName = `${normalizedName}.${fileExt}`;
      const filePath = `bank-logos/${fileName}`;

      const { data, error } = await this.supabase.storage
        .from('bank-logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) {
        console.error('Error uploading bank logo:', error);
        throw error;
      }

      // Get public URL
      const { data: urlData } = this.supabase.storage
        .from('bank-logos')
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (err) {
      console.error('Error in uploadBankLogo:', err);
      return null;
    }
  }

  /**
   * Get or create default logo for unknown banks
   */
  async getDefaultLogo(): Promise<BankLogo | null> {
    if (!this.supabaseTableAvailable) {
      return this.createLocalLogo('Default', 'default', this.getLocalDefaultLogoUrl());
    }

    try {
      // Try to find a default logo
      const { data, error } = await this.supabase
        .from('bank_logos')
        .select('*')
        .eq('bank_name', 'Default')
        .single();

      if (error && error.code === 'PGRST116') {
        const defaultLogoUrl = `${SUPABASE_URL}/storage/v1/object/public/bank-logos/default.png`;

        return await this.createBankLogo({
          bank_name: 'Default',
          logo_url: defaultLogoUrl,
          logo_storage_path: 'bank-logos/default.png'
        });
      }

      if (error) {
        if (this.handleTableError(error)) {
          return this.createLocalLogo('Default', 'default', this.getLocalDefaultLogoUrl());
        }
        throw error;
      }

      return data;
    } catch (err) {
      if (this.handleTableError(err)) {
        return this.createLocalLogo('Default', 'default', this.getLocalDefaultLogoUrl());
      }
      console.error('Error in getDefaultLogo:', err);
      return null;
    }
  }

  /**
   * Get logo URL for a bank (with fallback)
   */
  async getLogoUrlForBank(bankName: string): Promise<string> {
    if (!bankName) {
      return this.getLocalDefaultLogoUrl();
    }

    const localLogoImmediate = this.getLocalLogoUrl(bankName);
    if (localLogoImmediate) {
      return localLogoImmediate;
    }

    if (this.supabaseTableAvailable) {
      try {
        const bankLogo = await this.getBankLogoByBankName(bankName);
        if (bankLogo?.logo_url) {
          return bankLogo.logo_url;
        }

        const searchResults = await this.searchBankLogos(bankName);
        if (searchResults.length > 0 && searchResults[0]?.logo_url) {
          return searchResults[0].logo_url;
        }

        const normalizedName = this.normalizeBankName(bankName);
        const allLogos = await this.getAllBankLogos();

        const matchedLogo = allLogos.find(logo =>
          this.normalizeBankName(logo.bank_name) === normalizedName
        );

        if (matchedLogo?.logo_url) {
          return matchedLogo.logo_url;
        }
      } catch (err) {
        console.error('Error retrieving bank logo from Supabase:', err);
      }
    }

    const defaultLogo = await this.getDefaultLogo();
    return defaultLogo?.logo_url || this.getLocalDefaultLogoUrl();
  }

  /**
   * Normalize bank name for consistent matching
   */
  normalizeBankName(bankName: string): string {
    return this.normalizeForAsset(bankName);
  }

  /**
   * Cache management for performance
   */
  private cache: Map<string, { data: any; timestamp: number; ttl: number }> = new Map();

  private getCachedData(key: string): any | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data;
    }
    return null;
  }

  private setCachedData(key: string, data: any, ttl: number = 300000): void { // 5 minutes default TTL
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  /**
   * Get all bank logos with caching
   */
  async getAllBankLogosCached(): Promise<BankLogo[]> {
    const cacheKey = 'all_bank_logos';
    const cached = this.getCachedData(cacheKey);

    if (cached) {
      return cached;
    }

    const logos = await this.getAllBankLogos();
    this.setCachedData(cacheKey, logos, 600000); // 10 minutes cache
    return logos;
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
  }
}

// Export singleton instance
export const bankLogoService = new BankLogoService();
export default bankLogoService;
